import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import Dashboard from "@/pages/Dashboard";
import Setup from "@/pages/Setup";
import Onboarding from "@/pages/Onboarding";
import Landing from "@/pages/Landing";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import DisplayMode from "@/pages/DisplayMode";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PetsPage from "@/pages/PetsPage";
import PetsLanding from "@/pages/PetsLanding";
import HistoryPage from "@/pages/HistoryPage";
import PetHistoryPage from "@/pages/PetHistoryPage";
import TodoSharePage from "@/pages/TodoSharePage";
import KitchenPoster from "@/pages/KitchenPoster";
import Reviews from "@/pages/Reviews";
import EventInfoPage from "@/pages/EventInfoPage";
import { api, auth } from "@/lib/api";
import { setTimeFormat } from "@/lib/time";
import { isDialogOpen } from "@/lib/dialogPolling";
import { shouldAutoLaunchDisplay } from "@/lib/kioskMode";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/lib/i18n";

// Top-level error boundary. Without this, any uncaught render error
// unmounts the whole React tree and Android/iOS show a blank white
// page — no way for the user (or the developer they message) to know
// what happened. This catches the throw and shows a helpful shell with
// the actual error text plus a Reload / Sign-out escape.
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    // Surface to devtools so remote debugging picks it up.
    try {
      // eslint-disable-next-line no-console
      console.error("[FamFeel crash]", err, info?.componentStack);
    } catch {}
  }
  render() {
    if (this.state.err) {
      const msg = (this.state.err && (this.state.err.message || String(this.state.err))) || "Unknown error";
      const stackText = (this.state.err && this.state.err.stack) || "";
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background: "#F9F9F8",
            color: "#1c1c1c",
            fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <div style={{ maxWidth: 520, textAlign: "left" }}>
            <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 12, letterSpacing: "-0.01em" }}>
              Something crashed loading FamFeel.
            </div>
            <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
              This should never happen. Please tap Reload — if it keeps
              happening, sign out and back in.
            </div>
            <pre
              data-testid="app-crash-message"
              style={{
                fontSize: 12,
                background: "#efefec",
                border: "1px solid #d9d9d3",
                borderRadius: 8,
                padding: 10,
                overflow: "auto",
                maxHeight: 200,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {msg}
              {stackText ? "\n\n" + stackText.split("\n").slice(0, 6).join("\n") : ""}
            </pre>
            <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: "10px 18px",
                  background: "#1c1c1c",
                  color: "#F9F9F8",
                  border: "none",
                  borderRadius: 999,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Reload
              </button>
              <button
                type="button"
                onClick={() => {
                  try {
                    localStorage.removeItem("famfeel_family_token");
                    localStorage.removeItem("famfeel_family_id");
                    localStorage.removeItem("famfeel_family_name");
                  } catch {}
                  window.location.href = "/";
                }}
                style={{
                  padding: "10px 18px",
                  background: "transparent",
                  color: "#1c1c1c",
                  border: "1px solid #1c1c1c",
                  borderRadius: 999,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Sign out & go home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppShell() {
  const [authed, setAuthed] = useState(!!auth.getToken());
  const [settings, setSettings] = useState(null);
  const [members, setMembers] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [messages, setMessages] = useState([]);
  const [viewerId, setViewerIdState] = useState(
    typeof window !== "undefined" ? localStorage.getItem("famfeel_viewer_id") : null
  );
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-launch the ambient display when this device has been marked as the
  // kitchen display AND the app is running in standalone (installed PWA) mode.
  // Fires ONCE per browser session — after that the user can navigate freely
  // between /app ↔ /event/:id ↔ /display without being teleported back.
  // (AppShell unmounts on /display and /event/:id and remounts on return; the
  // session flag is what stops every remount from firing the redirect again.)
  useEffect(() => {
    if (!authed) return;
    if (location.pathname === "/display") return;
    let alreadyRedirected = false;
    try {
      alreadyRedirected =
        sessionStorage.getItem("ff_kiosk_auto_launched") === "1";
    } catch { /* private mode → behave as if it's the first launch */ }
    if (alreadyRedirected) return;
    if (shouldAutoLaunchDisplay()) {
      try { sessionStorage.setItem("ff_kiosk_auto_launched", "1"); } catch {}
      navigate("/display", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const setViewerId = useCallback((id) => {
    setViewerIdState(id);
    if (id) localStorage.setItem("famfeel_viewer_id", id);
    else localStorage.removeItem("famfeel_viewer_id");
    // Re-tag this device's existing push subscription (if any) to the
    // newly-active viewer. Without this, a phone that was enrolled
    // while Mum was the active viewer keeps every push targeted at
    // Mum even after Dad picks his card — meaning walk reminders,
    // nudges and event pings to Dad never reach this device. Best-
    // effort: a network failure here doesn't undo the local switch.
    try {
      // Lazy import keeps push.js out of the cold path for browsers
      // that don't support it (resyncPush itself is feature-gated).
      import("@/lib/push").then(({ resyncPush }) => {
        resyncPush(id || null).catch(() => {/* noop */});
      }).catch(() => {/* noop */});
    } catch { /* noop */ }
  }, []);

  const [loadError, setLoadError] = useState(null);

  const loadAll = useCallback(async () => {
    const viewerParam = viewerId ? { viewer_id: viewerId } : undefined;
    try {
      const [s, m, c] = await Promise.all([
        api.getFamilySettings(),
        api.listMembers(),
        api.todayCheckins(viewerParam),
      ]);
      setSettings(s);
      // Stamp the family's chosen time format onto the global so every
      // `fmtTime` call across the app picks it up — no prop-drilling.
      setTimeFormat(s?.time_format);
      setMembers(m);
      setCheckins(c);
      setLoadError(null);
      document.title = `FamFeel · ${s.family_name}`;
    } catch (err) {
      // Prod backend hiccup, stale token, or Cloudflare 5xx — surface it
      // with a retry button instead of stranding the user on an infinite
      // "Loading FamFeel…" placeholder.
      const status = err?.response?.status;
      setLoadError({
        status: status || null,
        message:
          status === 401 || status === 403
            ? "Your session expired. Please open your family again."
            : "Can't reach FamFeel right now. Please try again in a moment.",
      });
      throw err;
    }
  }, [viewerId]);

  useEffect(() => {
    if (!authed) return;
    loadAll().catch(() => {});
    const interval = setInterval(async () => {
      // Skip while any check-in/pet/event dialog is open — keeps mobile
      // keyboards from collapsing when the note textarea is in focus.
      if (isDialogOpen()) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      try {
        const c = await api.todayCheckins(viewerId ? { viewer_id: viewerId } : undefined);
        setCheckins(c);
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [authed, loadAll, viewerId]);

  useEffect(() => {
    if (members && viewerId && !members.find((m) => m.id === viewerId)) {
      setViewerId(null);
    }
  }, [members, viewerId, setViewerId]);

  // Preserve a ?join=XXX query param from a QR scan when redirecting to onboarding
  const onboardingSearch = location.search || "";

  if (!authed) {
    return (
      <Onboarding
        key={onboardingSearch}
        onReady={() => setAuthed(true)}
      />
    );
  }

  if (settings === null || members === null) {
    if (loadError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center px-6"
          data-testid="app-load-error"
        >
          <div className="max-w-sm text-center space-y-4">
            <div className="font-display-italic text-3xl">We can't reach FamFeel.</div>
            <p className="text-[color:var(--ink-2)] text-sm leading-relaxed">
              {loadError.message}
            </p>
            <div className="flex flex-col gap-2 items-center">
              <button
                type="button"
                onClick={() => {
                  setLoadError(null);
                  loadAll().catch(() => {});
                }}
                className="ff-btn ff-btn-primary"
                data-testid="app-load-retry"
              >
                Try again
              </button>
              {(loadError.status === 401 || loadError.status === 403) && (
                <button
                  type="button"
                  onClick={() => {
                    auth.setToken(null);
                    setAuthed(false);
                    setLoadError(null);
                  }}
                  className="ff-btn"
                  data-testid="app-load-signout"
                >
                  Sign out this device
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-display text-2xl">Loading FamFeel…</div>
      </div>
    );
  }

  return members.length === 0 ? (
    <Setup onDone={loadAll} />
  ) : location.pathname.startsWith("/history/") ? (
    <HistoryPage members={members} />
  ) : location.pathname.startsWith("/pets/history/") ? (
    <PetHistoryPage members={members} />
  ) : location.pathname === "/app/pets" ? (
    <PetsPage
      members={members}
      checkins={checkins}
      viewerId={viewerId}
      onOpenUpgrade={() => {
        try {
          sessionStorage.setItem("ff_open_billing", "pet_care");
        } catch {}
        window.location.assign("/app");
      }}
    />
  ) : (
    <Dashboard
      settings={settings}
      members={members}
      checkins={checkins}
      messages={messages}
      onRefresh={loadAll}
      setCheckins={setCheckins}
      setMembers={setMembers}
      setMessages={setMessages}
      setSettings={setSettings}
      viewerId={viewerId}
      setViewerId={setViewerId}
      onSignOut={async () => {
        // Tell the server to kill this device's session row so the JWT
        // is revoked server-side, not just dropped from localStorage.
        // Fire-and-forget: if the call fails we still clear locally.
        try { await api.signOutThisDevice(); } catch {}
        auth.clear();
        window.location.href = "/";
      }}
    />
  );
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Start every page at the top (anchor links inside the page still work,
    // since hash navigation sets its own scroll position afterwards).
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AppErrorBoundary>
    <I18nProvider>
      <BrowserRouter>
      <div className="App" data-testid="app-root">
        <ScrollToTopOnRouteChange />
        <Toaster position="top-center" richColors duration={2000} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/display" element={<DisplayMode />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/app" element={<AppShell />} />
          <Route path="/app/pets" element={<AppShell />} />
          <Route path="/history/:memberId" element={<AppShell />} />
          <Route path="/pets/history/:petId" element={<AppShell />} />
          <Route path="/pets" element={<PetsLanding />} />
          <Route path="/lists/:token" element={<TodoSharePage />} />
          <Route path="/lists/:token/print" element={<TodoSharePage />} />
          <Route path="/family/poster" element={<KitchenPoster />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/event/:id" element={<EventInfoPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
    </I18nProvider>
    </AppErrorBoundary>
  );
}

export default App;
